import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./hooks/useAuth";
import { ChatProvider } from "./hooks/useChat";
import ScrollToTop from "./components/ScrollToTop";
import RouteFallback from "./components/RouteFallback";
import { ProtectedRoute } from "./components/ProtectedRoute";
import WorkerJobsProtectedRoute from "./components/WorkerJobsProtectedRoute";

// Route-level code splitting
const GlobalChat = lazy(() => import("./components/GlobalChat"));
const Index = lazy(() => import("./pages/Index"));
const ConstructorSearch = lazy(() => import("./pages/ConstructorSearch"));
const AIMatching = lazy(() => import("./pages/AIMatching"));
const Auth = lazy(() => import("./pages/Auth"));
const HomeownerDashboard = lazy(() => import("./pages/HomeownerDashboard"));
const ContractorDashboard = lazy(() => import("./pages/ContractorDashboard"));
const Messages = lazy(() => import("./pages/Messages"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BudgetSimulator = lazy(() => import("./pages/BudgetSimulator"));
const ContractorLeads = lazy(() => import("@/pages/ContractorLeads"));
const HomeownerRequests = lazy(() => import("@/pages/HomeownerRequests"));
const HomeownerRequestDetail = lazy(() => import("@/pages/HomeownerRequestDetail"));
const HomeownerLeadDetails = lazy(() => import("@/pages/HomeownerLeadDetails"));
const HomeownerProposalDetails = lazy(() => import("@/pages/HomeownerProposalDetails"));
const ContractorLeadDetails = lazy(() => import("@/pages/ContractorLeadDetails"));
const LeadsMarketplace = lazy(() => import("@/pages/LeadsMarketplace"));
const LeadDetails = lazy(() => import("@/pages/LeadDetails"));
const JobsHomeowner = lazy(() => import("@/components/homeowner-dash/JobsHomeowner"));
const JobsContractor = lazy(() => import("@/components/contractor-dash/JobsContractor"));
const JobApplications = lazy(() => import("@/pages/JobApplicationsList"));
const JobApplicationDetail = lazy(() => import("@/pages/JobApplicationDetail"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const JobsEntry = lazy(() => import("@/pages/JobsEntry"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const HomeownerDocumentsPage = lazy(() => import("./pages/HomeownerDocumentsPage"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ChatProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<RouteFallback /> }>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/search" element={<ConstructorSearch />} />
                <Route path="/ai-matching" element={<AIMatching />} />
                <Route path="/budget-simulator" element={<BudgetSimulator />} />
                <Route path="/budget-simulator/start" element={<BudgetSimulator />} />
                <Route path="/budget-simulator/start/:projectType/:step" element={<BudgetSimulator />} />
                <Route path="/leads" element={<LeadsMarketplace />} />
                <Route path="/leads/:id" element={<LeadDetails />} />
                {/* Unified Jobs entry point (decides internally what to show) */}
                <Route path="/jobs" element={<JobsEntry />} />

                {/* Auth Routes */}
                <Route path="/auth/*" element={<Auth />} />
                <Route path="/auth/register" element={<Auth />} />

                {/* Homeowner Dashboard Routes */}
                <Route
                  path="/dashboard/homeowner"
                  element={
                    <ProtectedRoute allowedRoles={["homeowner"]}>
                      <HomeownerDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/homeowner/requests"
                  element={
                    <ProtectedRoute allowedRoles={["homeowner"]}>
                      <HomeownerRequests />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/homeowner/requests/:quoteId"
                  element={
                    <ProtectedRoute allowedRoles={["homeowner"]}>
                      <HomeownerRequestDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/homeowner/leads/:leadId"
                  element={
                    <ProtectedRoute allowedRoles={["homeowner"]}>
                      <HomeownerLeadDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/homeowner/proposals/:proposalId"
                  element={
                    <ProtectedRoute allowedRoles={["homeowner"]}>
                      <HomeownerProposalDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/homeowner/jobs"
                  element={
                    <ProtectedRoute allowedRoles={["homeowner"]}>
                      <WorkerJobsProtectedRoute>
                        <JobsHomeowner />
                      </WorkerJobsProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/homeowner/documents"
                  element={
                    <ProtectedRoute allowedRoles={["homeowner"]}>
                      <HomeownerDocumentsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Contractor Dashboard Routes */}
                <Route
                  path="/dashboard/contractor"
                  element={
                    <ProtectedRoute allowedRoles={["contractor"]}>
                      <ContractorDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/contractor/leads"
                  element={
                    <ProtectedRoute allowedRoles={["contractor"]}>
                      <ContractorLeads />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <ProtectedRoute allowedRoles={["contractor"]}>
                      <DocumentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/contractor/leads/:leadId"
                  element={
                    <ProtectedRoute allowedRoles={["contractor"]}>
                      <ContractorLeadDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/contractor/jobs"
                  element={
                    <ProtectedRoute allowedRoles={["contractor"]}>
                      <JobsContractor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/contractor/jobs/:jobId"
                  element={
                    <ProtectedRoute allowedRoles={["contractor"]}>
                      <JobDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/contractor/jobs/:jobId/applications"
                  element={
                    <ProtectedRoute allowedRoles={["contractor"]}>
                      <JobApplications />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/contractor/jobs/:jobId/applications/:applicationId"
                  element={
                    <ProtectedRoute allowedRoles={["contractor"]}>
                      <JobApplicationDetail />
                    </ProtectedRoute>
                  }
                />

                {/* Shared Dashboard Routes */}
                <Route
                  path="/dashboard/messages"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <Messages />
                    </ProtectedRoute>
                  }
                />

                {/* Default dashboard redirect based on role */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <HomeownerDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                {/* Backwards compatibility: redirect old worker profile path to main profile */}
                <Route path="/worker-profile" element={<Navigate to="/profile" replace />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <Suspense fallback={null}>
              <GlobalChat />
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </ChatProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
